import * as React from "react";
import { scaleLinear } from "d3-scale";

const ATR = React.lazy(() => import("./Indicators/ATR"));
const RSI = React.lazy(() => import("./Indicators/RSI"));
const MACD = React.lazy(() => import("./Indicators/MACD"));
const Stochastic = React.lazy(() => import("./Indicators/Stochastic"));
const ROC = React.lazy(() => import("./Indicators/ROC"));
const HeikenAshi = React.lazy(() => import("./Indicators/HeikenAshi"));

export const availableOscillators = ["RSI", "MACD", "Stoch", "ROC", "ATR", "HeikenAshi"];

export function getRelativeHeight(height: number, relativeHeightInPercents: number) {
  return Math.floor(height * (relativeHeightInPercents / 100));
}

export function getOscillatorHeightInPercents(itemsNumber: number) {
  const MAX_HEIGHT_IN_PERCENTS = 60;
  const MIN_HEIGHT_IN_PERCENTS = 20;
  const HEIGHT_PER_ITEM_IN_PERCENTS = 20;

  if (!itemsNumber) {
    return 0;
  }

  const heightInPercents = Math.min(
    MIN_HEIGHT_IN_PERCENTS + HEIGHT_PER_ITEM_IN_PERCENTS * (itemsNumber - 1),
    MAX_HEIGHT_IN_PERCENTS
  );

  return heightInPercents;
}

export function getScaleModifier(indicators: Array<Object>) {
  const enabledIndicators = indicators.filter(indicator => indicator.enabled !== false);

  let oscillators = [];
  if (enabledIndicators && enabledIndicators.length) {
    oscillators = enabledIndicators.filter(
      indicator => indicator.isOscillator === true && availableOscillators.indexOf(indicator.type) > -1
    );
  }

  const oscillatorHeightInPercents = getOscillatorHeightInPercents(oscillators.length);
  const scaleModifier = 1 - oscillatorHeightInPercents / 100;

  return scaleModifier;
}

export default function OscillatorVisuals(props) {
  const {
    stock,
    width,
    height,
    oscillatorHeight,
    points,
    rawData,
    indicators,
    scaleX,
    scaleY,
    interval,
    onRemove,
    onHide,
    onEdit,
    mobile
  } = props;

  return (
  <React.Suspense fallback={null}>
    <div style={{ height, cursor: "default" }}>
      {indicators.map((indicator, index) => {
        if (!indicator.isOscillator || indicator.enabled === false) {
          return null;
        }

        switch (indicator.type) {
          case "HeikenAshi":
            return <HeikenAshi
              key={index}
              stock={stock}
              indicatorIndex={index}
              width={width}
              config={indicator}
              height={oscillatorHeight}
              points={rawData}
              scaleX={scaleX}
              scaleY={scaleY}
              onHide={onHide}
              onEdit={onEdit}
              onRemove={onRemove}
              interval={interval}
              mobile={mobile}
              mouseX={props.mouseX}
              isHovered={props.isHovered}
            />;
          case "ATR":
            return <ATR
              key={index}
              indicatorIndex={index}
              width={width}
              height={oscillatorHeight}
              config={indicator}
              points={points}
              scaleX={scaleX}
              onRemove={onRemove}
              onHide={onHide}
              onEdit={onEdit}
              interval={interval}
              mobile={mobile}
            />;
          case "RSI":
            const scaleYRSI = scaleLinear()
              .domain([0, 100])
              .range([oscillatorHeight - 10, 10]);

            return <RSI
              key={index}
              indicatorIndex={index}
              width={width}
              height={oscillatorHeight}
              config={indicator}
              points={points}
              scaleX={scaleX}
              scaleY={scaleYRSI}
              onRemove={onRemove}
              onHide={onHide}
              onEdit={onEdit}
              interval={interval}
              mobile={mobile}
            />;
          case "MACD":
            return <MACD
              key={index}
              stock={stock}
              indicatorIndex={index}
              width={width}
              height={oscillatorHeight}
              config={indicator}
              points={points}
              scaleX={scaleX}
              onRemove={onRemove}
              onHide={onHide}
              onEdit={onEdit}
              interval={interval}
              mobile={mobile}
            />;
          case "ROC":
            return <ROC
              key={index}
              stock={stock}
              indicatorIndex={index}
              width={width}
              height={oscillatorHeight}
              config={indicator}
              points={points}
              scaleX={scaleX}
              onRemove={onRemove}
              onHide={onHide}
              onEdit={onEdit}
              interval={interval}
              mobile={mobile}
            />;
          case "Stoch":
            const scaleYStoch = scaleLinear()
              .domain([0, 100])
              .range([oscillatorHeight - 10, 10]);

            return <Stochastic
              key={index}
              indicatorIndex={index}
              width={width}
              height={oscillatorHeight}
              config={indicator}
              points={points}
              scaleX={scaleX}
              scaleY={scaleYStoch}
              onRemove={onRemove}
              onHide={onHide}
              onEdit={onEdit}
              interval={interval}
              mobile={mobile}
            />;
          default:
            return null;
        }
      })}
    </div>
  </React.Suspense>
  );
}



// WEBPACK FOOTER //
// ./src/components/Chart/OscillatorVisuals.js